<template>
    <Dialog :visible="visible" @update:visible="v => $emit('update:visible', v)" modal :header="title"
        :style="{ width: '60vw' }" :breakpoints="{ '1199px': '85vw', '575px': '95vw' }" :closable="!sending">
        <div v-if="loading" class="text-center py-5">
            <i class="pi pi-spin pi-spinner" style="font-size: 1.5rem;"></i>
            <p class="text-muted mt-2 mb-0">جاري تحميل قائمة المستلمين...</p>
        </div>

        <template v-else>
            <div v-if="blockedMessage" class="alert alert-warning mb-0">{{ blockedMessage }}</div>

            <template v-else>
                <!-- ── Summary ── -->
                <div class="row g-2 mb-3 text-center">
                    <div class="col-6 col-md-2">
                        <div class="np-stat"><div class="np-stat__value">{{ summary.total_eligible ?? 0 }}</div><div class="np-stat__label">إجمالي المستحقين</div></div>
                    </div>
                    <div class="col-6 col-md-2">
                        <div class="np-stat np-stat--success"><div class="np-stat__value">{{ summary.total_recipients ?? 0 }}</div><div class="np-stat__label">أولياء الأمور المستلمون</div></div>
                    </div>
                    <div class="col-6 col-md-2">
                        <div class="np-stat np-stat--whatsapp"><div class="np-stat__value">{{ summary.whatsapp ?? 0 }}</div><div class="np-stat__label">WhatsApp</div></div>
                    </div>
                    <div class="col-6 col-md-2">
                        <div class="np-stat np-stat--sms"><div class="np-stat__value">{{ summary.sms ?? 0 }}</div><div class="np-stat__label">SMS</div></div>
                    </div>
                    <div class="col-6 col-md-2">
                        <div class="np-stat np-stat--warning"><div class="np-stat__value">{{ summary.no_phone ?? 0 }}</div><div class="np-stat__label">بدون رقم ولي أمر</div></div>
                    </div>
                    <div class="col-6 col-md-2">
                        <div class="np-stat np-stat--muted"><div class="np-stat__value">{{ summary.excluded_online ?? 0 }}</div><div class="np-stat__label">أونلاين (مستبعدون)</div></div>
                    </div>
                </div>

                <div v-if="!whatsappAvailable" class="alert alert-info py-2 small">
                    <i class="pi pi-info-circle me-1"></i>
                    واتساب غير متصل حالياً — سيتم إرسال الإشعارات عبر SMS لجميع المستلمين بدلاً من واتساب.
                </div>

                <!-- ── Recipients table ── -->
                <div class="np-table-wrap">
                    <table class="table table-sm table-striped mb-0">
                        <thead>
                            <tr>
                                <th>الطالب</th>
                                <th>رقم الطالب</th>
                                <th>نظام الدراسة</th>
                                <th>رقم ولي الأمر</th>
                                <th>القناة</th>
                                <th>السبب</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="r in recipients" :key="r.student_id">
                                <td>{{ r.student_name }}</td>
                                <td>{{ r.student_number }}</td>
                                <td>{{ studyModeLabel(r.study_mode) }}</td>
                                <td dir="ltr">{{ r.parent_phone }}</td>
                                <td>
                                    <span class="badge" :class="r.channel === 'whatsapp' ? 'bg-success' : 'bg-info text-dark'">
                                        {{ r.channel === 'whatsapp' ? 'WhatsApp' : 'SMS' }}
                                    </span>
                                </td>
                                <td>{{ r.reason }}</td>
                            </tr>
                        </tbody>
                    </table>
                    <p v-if="!recipients.length" class="text-muted text-center py-4 mb-0">
                        لا يوجد طلاب مستحقون للإشعار حالياً بحسب الفلاتر الحالية.
                    </p>
                </div>

                <!-- ── No phone warning list ── -->
                <div v-if="noPhone.length" class="mt-3">
                    <div class="alert alert-warning mb-2">
                        <strong>طلاب بدون رقم ولي أمر</strong> — لن يتم إرسال أي إشعار لهم (لن يُستخدم رقم الطالب بديلاً):
                    </div>
                    <ul class="np-no-phone-list mb-0">
                        <li v-for="s in noPhone" :key="s.student_id">{{ s.student_name }} (#{{ s.student_number }})</li>
                    </ul>
                </div>
            </template>
        </template>

        <template #footer>
            <button type="button" class="btn btn-secondary" @click="$emit('cancel')" :disabled="sending">إلغاء</button>
            <button type="button" class="btn btn-warning" @click="$emit('confirm')"
                :disabled="sending || loading || !!blockedMessage || !recipients.length">
                <i class="pi pi-spin pi-spinner-dotted me-1" v-if="sending"></i>
                {{ sending ? 'جاري الإرسال...' : 'تأكيد وإرسال' }}
            </button>
        </template>
    </Dialog>
</template>

<script>
import Dialog from 'primevue/dialog';

export default {
    name: 'NotificationPreviewDialog',
    components: { Dialog },
    props: {
        visible: { type: Boolean, default: false },
        title: { type: String, default: 'معاينة الإشعار' },
        loading: { type: Boolean, default: false },
        sending: { type: Boolean, default: false },
        blockedMessage: { type: String, default: null },
        whatsappAvailable: { type: Boolean, default: true },
        summary: { type: Object, default: () => ({}) },
        recipients: { type: Array, default: () => [] },
        noPhone: { type: Array, default: () => [] },
    },
    emits: ['update:visible', 'confirm', 'cancel'],
    methods: {
        studyModeLabel(mode) {
            return { offline: 'حضوري', hybrid: 'هجين', online: 'أونلاين' }[mode] ?? '—';
        },
    },
};
</script>

<style scoped>
.np-stat {
    background: #f8f9fa;
    border-radius: 8px;
    padding: 0.5rem 0.25rem;
}
.np-stat__value { font-size: 1.25rem; font-weight: 700; }
.np-stat__label { font-size: 0.72rem; color: #6c757d; }
.np-stat--success { background: #eafaf0; }
.np-stat--success .np-stat__value { color: #198754; }
.np-stat--whatsapp { background: #eafaf0; }
.np-stat--whatsapp .np-stat__value { color: #25d366; }
.np-stat--sms { background: #e7f5ff; }
.np-stat--sms .np-stat__value { color: #0d6efd; }
.np-stat--warning { background: #fff8e6; }
.np-stat--warning .np-stat__value { color: #b45309; }
.np-stat--muted { background: #f1f1f1; }
.np-stat--muted .np-stat__value { color: #6c757d; }

.np-table-wrap {
    max-height: 42vh;
    overflow-y: auto;
    border: 1px solid #e9ecef;
    border-radius: 6px;
}
.np-table-wrap table thead th {
    position: sticky;
    top: 0;
    background: #fff;
    z-index: 1;
}
.np-no-phone-list {
    max-height: 15vh;
    overflow-y: auto;
    font-size: 0.85rem;
    padding-inline-start: 1.25rem;
}
</style>
